/**
 * Airplane Data Module for Ponder Flight Game
 * Provides optimized access to airplane model data.
 * 
 * Data Source Priority:
 * 1. Server-provided data (via setAirplaneModels) - authoritative source
 * 2. Local airplanes.json - fallback for initial render before server sync
 */

import airplanesJSON from './airplanes.json';

/**
 * AirplaneModel represents a base aircraft model from airplanes.json
 */
export interface AirplaneModel {
  model_number: number;
  /** Base model category (0-7) that this variant belongs to */
  base_model: number;
  model_name: string;
  body_type: 'regional' | 'narrow' | 'wide_9across' | 'wide_10across';
  cabin_length: number;
  oew: number;
  mtow: number;
  fuel_capacity: number;
  cruise_speed: number;
  pilot_count: number;
  average_person_weight: number;
  fuel_burn_base: number;
  fuel_burn_weight: number;
  cabin_comfort_modifier: number;
  cabin_service_cost_modifier: number;
  cabin_capacity_modifier: number;
  max_seats: number;
  range: number;
  speed: number;
  cost: number;
  /** Hourly crew operating expense in dollars */
  opex_hour_crew: number;
  /** Per-passenger service cost for economy class in dollars */
  per_passenger_service_econ: number;
  /** Per-passenger service cost for business class in dollars */
  per_passenger_service_business: number;
  /** Per-passenger service cost for first class in dollars */
  per_passenger_service_first: number;
  /** Per-flight ground handling expense in dollars */
  opex_flight_ground: number;
  category: number;
  fuel_use: number;
  /** Fully-loaded weight in metric tons (1000 kg) */
  loaded_weight: number;
  base_comfort_score: [number, number, number];
}

type RawAirplaneModel = Partial<AirplaneModel> & Pick<AirplaneModel, 'model_number' | 'base_model' | 'model_name'>;

function defaultAirframeBody(maxSeats: number): AirplaneModel['body_type'] {
  if (maxSeats <= 100) return 'regional';
  if (maxSeats <= 240) return 'narrow';
  if (maxSeats <= 500) return 'wide_9across';
  return 'wide_10across';
}

function defaultSeatsPerRow(bodyType: AirplaneModel['body_type']): number {
  switch (bodyType) {
    case 'regional':
      return 4;
    case 'wide_9across':
      return 9;
    case 'wide_10across':
      return 10;
    default:
      return 6;
  }
}

function defaultCabinLength(bodyType: AirplaneModel['body_type'], maxSeats: number): number {
  const rows = Math.ceil(maxSeats / defaultSeatsPerRow(bodyType));
  const facilityAllowance = bodyType === 'regional' ? 320 : bodyType.startsWith('wide_') ? 800 : 480;
  return Math.round(rows * 82 + facilityAllowance);
}

function defaultPilotCount(_maxSeats: number): number {
  return 2;
}

function normalizeAirplaneModel(raw: RawAirplaneModel): AirplaneModel {
  const maxSeats = raw.max_seats ?? 0;
  const speed = raw.speed ?? raw.cruise_speed ?? 0;
  const bodyType = raw.body_type ?? defaultAirframeBody(maxSeats);

  return {
    model_number: raw.model_number,
    base_model: raw.base_model,
    model_name: raw.model_name,
    body_type: bodyType,
    cabin_length: raw.cabin_length ?? defaultCabinLength(bodyType, maxSeats),
    oew: raw.oew ?? 0,
    mtow: raw.mtow ?? 0,
    fuel_capacity: raw.fuel_capacity ?? 0,
    cruise_speed: raw.cruise_speed ?? speed,
    pilot_count: raw.pilot_count ?? defaultPilotCount(maxSeats),
    average_person_weight: raw.average_person_weight ?? 95,
    fuel_burn_base: raw.fuel_burn_base ?? 0,
    fuel_burn_weight: raw.fuel_burn_weight ?? 0,
    cabin_comfort_modifier: raw.cabin_comfort_modifier ?? 0,
    cabin_service_cost_modifier: raw.cabin_service_cost_modifier ?? 0,
    cabin_capacity_modifier: raw.cabin_capacity_modifier ?? 0,
    max_seats: maxSeats,
    range: raw.range ?? 0,
    speed,
    cost: raw.cost ?? 0,
    opex_hour_crew: raw.opex_hour_crew ?? 0,
    per_passenger_service_econ: raw.per_passenger_service_econ ?? 0,
    per_passenger_service_business: raw.per_passenger_service_business ?? 0,
    per_passenger_service_first: raw.per_passenger_service_first ?? 0,
    opex_flight_ground: raw.opex_flight_ground ?? 0,
    category: raw.category ?? 0,
    fuel_use: raw.fuel_use ?? 0,
    loaded_weight: raw.loaded_weight ?? 0,
    base_comfort_score: raw.base_comfort_score ?? [45, 60, 72],
  };
}

/** Maps innovation IDs to their corresponding model numbers */
const InnovationToModelNumber: Record<string, number> = {
  'sparrow_mark2_v2': 7,
  'sparrow_ev_v3': 8,
  'swift_plus_v2': 9,
  'swift_pro_v3': 10,
  'hawk_v1': 2,
  'hawk_xl_v2': 11,
  'hawk_xlr_v3': 12,
  'eagle_v1': 3,
  'eagle_liberty_v2': 13,
  'eagle_freedom_v3': 14,
  'condor_v1': 4,
  'condor_max_v2': 15,
  'condor_ultra_v3': 16,
  'pelican_v1': 5,
  'pelican_super_v2': 17,
  'falcon_v1': 6,
  'falcon_ultimate_v2': 18,
  'osprey_v1': 19,
  'osprey_lr_v2': 20,
};

/** Base model numbers for each plane category (v1 models) */
export const BaseModelNumbers: number[] = [0, 1, 2, 3, 4, 5, 6, 7];

/** Number of base plane categories */
export const NUM_BASE_MODELS = 8;

/**
 * The innovations that bring each family's models, in order. For a family
 * that must be researched at all, the first is its v1; the rest are its
 * upgrades. Mirrors the chains in airplanes.go's GetCurrentModelForBase.
 */
const FAMILY_INNOVATIONS: Record<number, string[]> = {
  0: ['sparrow_mark2_v2', 'sparrow_ev_v3'],
  1: ['swift_plus_v2', 'swift_pro_v3'],
  2: ['hawk_v1', 'hawk_xl_v2', 'hawk_xlr_v3'],
  3: ['eagle_v1', 'eagle_liberty_v2', 'eagle_freedom_v3'],
  4: ['condor_v1', 'condor_max_v2', 'condor_ultra_v3'],
  5: ['pelican_v1', 'pelican_super_v2'],
  6: ['falcon_v1', 'falcon_ultimate_v2'],
  7: ['osprey_v1', 'osprey_lr_v2'],
};

/**
 * Whether a team may configure a family (base model 0–7): the Sparrow and the
 * Swift always, any other once its v1 is researched. Mirrors
 * IsPlaneSlotUnlocked in airplanes.go.
 */
export function isFamilyUnlocked(baseModel: number, unlockedInnovations: Record<string, boolean>): boolean {
  if (baseModel < 2) return true;
  const v1 = FAMILY_INNOVATIONS[baseModel]?.[0];
  return v1 !== undefined && unlockedInnovations[v1] === true;
}

/**
 * The model number a team gets for a family today: its v1, or the latest
 * upgrade it has researched. Families 0–6 number their v1 as the family;
 * the Osprey's v1 is model 19. Mirrors GetCurrentModelForBase in airplanes.go.
 */
export function currentModelForFamily(baseModel: number, unlockedInnovations: Record<string, boolean>): number {
  const chain = FAMILY_INNOVATIONS[baseModel];
  if (!chain) return baseModel;
  let model = baseModel >= 7 ? (InnovationToModelNumber[chain[0]] ?? baseModel) : baseModel;
  for (const innovation of chain) {
    if (unlockedInnovations[innovation]) model = InnovationToModelNumber[innovation] ?? model;
  }
  return model;
}

/** What an aircraft innovation does to its family, and where it sits in the family's models. */
export interface AircraftResearch {
  family: number;
  /** The family's models in order, each with the innovation that brings it — none for a model a team has from the start. */
  models: ReadonlyArray<{ model: number; innovationId: string | null }>;
  /** It brings the family into the picker (its v1), rather than upgrading a model the team has. */
  bringsFamily: boolean;
  /** The model it gives. */
  model: number;
  /** The model it upgrades; null for a v1. */
  replaces: number | null;
}

/**
 * The family an aircraft innovation belongs to and what it does there: a v1
 * brings the family; anything else turns the model before it in the chain —
 * the family's own for the Sparrow and the Swift, which every team has — into
 * its own. Null for an innovation that is not an aircraft's.
 */
export function aircraftResearch(innovationId: string): AircraftResearch | null {
  for (const [key, chain] of Object.entries(FAMILY_INNOVATIONS)) {
    const index = chain.indexOf(innovationId);
    if (index === -1) continue;
    const family = Number(key);
    const fromStart = family < 2;
    const models = [
      ...(fromStart ? [{ model: family, innovationId: null }] : []),
      ...chain.map((id) => ({ model: InnovationToModelNumber[id], innovationId: id })),
    ];
    const at = models.findIndex((entry) => entry.innovationId === innovationId);
    return {
      family,
      models,
      bringsFamily: !fromStart && index === 0,
      model: InnovationToModelNumber[innovationId],
      replaces: at > 0 ? models[at - 1].model : null,
    };
  }
  return null;
}

/**
 * Optimized airplane data access with O(1) lookups.
 * This class provides fast indexed access to airplane models.
 */
class AirplaneDatabase {
  private byModelNumber: Map<number, AirplaneModel> = new Map();
  private byBaseModel: Map<number, AirplaneModel[]> = new Map();
  private data: AirplaneModel[];
  private isServerData: boolean = false;

  constructor(airplanes: AirplaneModel[]) {
    this.data = airplanes.map(normalizeAirplaneModel);
    this.rebuildIndexes();
  }

  /** Rebuild lookup indexes after data changes */
  private rebuildIndexes(): void {
    this.byModelNumber = new Map(this.data.map(airplane => [airplane.model_number, airplane]));
    
    // Build index by base model
    this.byBaseModel = new Map();
    for (const airplane of this.data) {
      const existing = this.byBaseModel.get(airplane.base_model) || [];
      existing.push(airplane);
      this.byBaseModel.set(airplane.base_model, existing);
    }
  }

  /**
   * Update the airplane database with server-provided data.
   * This replaces the local data with authoritative server data.
   * @param models - Array of airplane models from server
   */
  setFromServer(models: AirplaneModel[]): void {
    console.log(`[AirplaneDatabase] Received ${models.length} airplane models from server`);
    this.data = models.map(normalizeAirplaneModel);
    this.isServerData = true;
    this.rebuildIndexes();
  }

  /** Check if data is from server (authoritative) or local fallback */
  isFromServer(): boolean {
    return this.isServerData;
  }

  /** Get all airplanes as an array */
  getAll(): AirplaneModel[] {
    return this.data;
  }

  /** Get airplane by model number (O(1) lookup) */
  getByModelNumber(modelNumber: number): AirplaneModel | undefined {
    return this.byModelNumber.get(modelNumber);
  }

  /** Get all variants for a base model (e.g., all Sparrow variants) */
  getVariantsByBaseModel(baseModel: number): AirplaneModel[] {
    return this.byBaseModel.get(baseModel) || [];
  }

  /** Get total number of airplane models */
  get count(): number {
    return this.data.length;
  }
}

/**
 * Singleton airplane database instance.
 * Initially loaded from local airplanes.json, but will be updated with
 * server-provided data when the client joins a match.
 * 
 * @example
 * ```typescript
 * import { airplanes } from './data/airplanes';
 * 
 * const sparrow = airplanes.getByModelNumber(0);
 * const allModels = airplanes.getAll();
 * ```
 */
export const airplanes = new AirplaneDatabase(airplanesJSON as RawAirplaneModel[] as AirplaneModel[]);

/**
 * Update the airplane database with server-provided data.
 * Call this when receiving airplane_models_state from the server.
 * @param models - Array of airplane models from server
 */
export function setAirplaneModels(models: AirplaneModel[]): void {
  airplanes.setFromServer(models);
}
